import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Laptop, RadioTower, Sun, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const LOGO = 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/a607bdba6afda6a2_LTI.png';
const PORTRAIT = 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/a2cb16385d223651_id.png';

const LiorTechnologiePage = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const poles = [
    {
      Icon: Laptop,
      tone: '#1a9641',
      title: t(language, 'Informatique', 'IT & Software'),
      desc: t(
        language,
        "Développement de solutions logicielles, infrastructures réseaux, maintenance informatique et accompagnement à la transformation digitale des entreprises.",
        'Software solutions development, network infrastructure, IT maintenance and support for companies in their digital transformation.'
      ),
    },
    {
      Icon: RadioTower,
      tone: '#1565c0',
      title: t(language, 'Gestion des communications', 'Communications management'),
      desc: t(
        language,
        "Déploiement et gestion de systèmes de télécommunications : réseaux d'entreprise, interconnexions de sites, solutions VSAT et radio professionnelles.",
        'Deployment and management of telecommunication systems: corporate networks, site interconnections, VSAT and professional radio solutions.'
      ),
    },
    {
      Icon: Sun,
      tone: '#FF8C00',
      title: t(language, 'Développement projet photovoltaïque', 'Photovoltaic project development'),
      desc: t(
        language,
        "Études, dimensionnement et réalisation d'installations solaires photovoltaïques pour résidences, entreprises et sites industriels, avec solutions de stockage adaptées.",
        'Studies, sizing and implementation of solar photovoltaic installations for homes, businesses and industrial sites, with suitable storage solutions.'
      ),
    },
  ];

  const synergies = [
    t(language, 'Installations solaires intégrées dès la conception des bâtiments GCI', 'Solar installations integrated from the design stage of GCI buildings'),
    t(language, 'Bâtiments intelligents : réseaux, connectivité et domotique', 'Smart buildings: networks, connectivity and home automation'),
    t(language, 'Développement et déploiement des infrastructures numériques pour la communication et gestion des entreprises', 'Development and deployment of digital infrastructure for business communication and management'),
    t(language, "Solutions d'énergie fiables adaptées au contexte de Kinshasa", 'Reliable energy solutions adapted to the Kinshasa context'),
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="lior-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_construction-global/artifacts/rfqy73ql_gestion-solaire-intelligente-getty-sl-f-1250x550.jpg')" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, rgba(17,24,39,0.92) 0%, rgba(17,24,39,0.75) 45%, rgba(17,24,39,0.35) 100%)' }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 py-20 lg:py-28">
          <Link
            to="/qui-sommes-nous/nos-partenaires"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 transition-colors"
            data-testid="lior-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t(language, 'Retour aux partenaires', 'Back to partners')}
          </Link>
          <p className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 mb-4">
            {t(language, 'Partenaire stratégique', 'Strategic partner')}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight">
            Lior Technologie & Ingénierie
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-2xl mt-4">
            {t(
              language,
              "Informatique, gestion des communications et développement de projets photovoltaïques au service des projets GCI.",
              'IT, communications management and photovoltaic project development serving GCI projects.'
            )}
          </p>
        </div>
      </section>

      {/* Présentation */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <img
                src={LOGO}
                alt="Lior Technologie & Ingénierie"
                className="w-full max-w-md object-contain"
                data-testid="lior-logo"
              />
            </div>
            <div>
              <p className="text-base font-semibold text-orange-500 uppercase tracking-wider mb-2">
                {t(language, 'Qui est Lior ?', 'Who is Lior?')}
              </p>
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#702963' }}>
                {t(language, 'Une expertise technologique au service du bâtiment', 'Technology expertise serving the building sector')}
              </h2>
              <div className="h-1 w-20 bg-orange-500 mb-6"></div>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t(
                  language,
                  "Lior Technologie & Ingénierie est une startup spécialisée dans deux domaines complémentaires : l'informatique, le développement et la gestion de projets photovoltaïques. Elle accompagne les entreprises et les particuliers dans la conception, le déploiement et la maintenance de solutions, énergétique, technologiques fiables et durables.",
                  'Lior Technologie & Ingénierie is a startup specialised in two complementary fields: IT, and the development and management of photovoltaic projects. It supports companies and individuals in designing, deploying and maintaining reliable and sustainable energy and technology solutions.'
                )}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  language,
                  "En partenariat avec GCI, LTI apporte la dimension énergétique et numérique (installations solaires, réseaux de communication et solutions informatiques intelligentes).",
                  'In partnership with GCI, LTI brings the energy and digital dimension (solar installations, communication networks and smart IT solutions).'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pôles d'expertise */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-2" style={{ color: '#702963' }}>
            {t(language, "Trois pôles d'expertise", 'Three areas of expertise')}
          </h2>
          <div className="h-1 w-20 bg-orange-500 mx-auto mb-12"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {poles.map((pole) => (
              <div
                key={pole.title}
                className="bg-white border border-gray-200 border-t-4 p-8 rounded-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                style={{ borderTopColor: pole.tone }}
                data-testid={`lior-pole-${pole.title}`}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${pole.tone}18` }}
                >
                  <pole.Icon className="w-7 h-7" style={{ color: pole.tone }} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{pole.title}</h3>
                <p className="text-gray-700 leading-relaxed text-sm">{pole.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Direction */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-base font-semibold text-orange-500 uppercase tracking-wider mb-2">
                {t(language, 'Direction', 'Leadership')}
              </p>
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#702963' }}>
                {t(language, 'Une vision portée par la jeunesse et la rigueur', 'A vision driven by youth and rigour')}
              </h2>
              <div className="h-1 w-20 bg-orange-500 mb-6"></div>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t(
                  language,
                  "Lior Technologie & Ingénierie a été créée en 2025 par M. NSINDU POYA Glodi (informaticien et cadre maîtrise des énergies renouvelables et infrastructures environnementales), convaincu que la technologie et l'énergie propre sont les leviers du développement de la RDC. Chaque projet est mené avec exigence, transparence et un sens aigu du service.",
                  'Lior Technologie & Ingénierie was founded in 2025 by Mr. NSINDU POYA Glodi (IT specialist and senior expert in renewable energies and environmental infrastructure), convinced that technology and clean energy are the levers of DRC development. Each project is carried out with rigour, transparency and a strong sense of service.'
                )}
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                {t(
                  language,
                  "Sa mission est d'accompagner les entreprises, les institutions et les particuliers dans l'accès à une énergie propre et à des infrastructures numériques fiables, de l'étude initiale jusqu'à l'exploitation.",
                  'Its mission is to support companies, institutions and individuals in accessing clean energy and reliable digital infrastructure, from the initial study through to operation.'
                )}
              </p>
              <ul className="space-y-3">
                {synergies.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-sm">
                <img
                  src={PORTRAIT}
                  alt={t(language, 'Direction de Lior Technologie & Ingénierie', 'Lior Technologie & Ingénierie leadership')}
                  className="w-full h-auto"
                  data-testid="lior-portrait"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14" style={{ background: '#702963' }}>
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t(language, 'Un projet solaire ou numérique avec GCI et Lior ?', 'A solar or digital project with GCI and Lior?')}
          </h2>
          <Link
            to="/votre-projet"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full transition-colors"
            data-testid="lior-cta-button"
          >
            {t(language, 'Décrire mon projet', 'Describe my project')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LiorTechnologiePage;
